/**
 * PolicyEditDialog — the "Bearbeiten" edit surface for a Police's manual Stammdaten
 * (DMS Part2 P2-4, WS4 §4.3/§4.8): Prämienbefreiung (ja/nein + seit-Datum),
 * Versicherungsvermittler (person-picker), Bemerkungen, and the Bezeichnung label.
 * PATCHes `apiClient.policies.update` (`PATCH /policies/:id`, `policy.write`).
 *
 * Mirrors `FirmEditDialog`'s draft/save shape (draft state owned here, PATCH sends
 * the full editable set, the server decides what actually changed).
 *
 * Person-picker deviation (flagged): there is no dedicated "list/search persons"
 * endpoint in this repo. `GET /customers?subject_type=person&q=` is the closest
 * existing search surface (already used 3× in this feature — `useKundeDetail`/
 * `usePoliciesForKunde`/`usePoliceDetail`) — it only searches persons already lifted
 * into the customer lifecycle (`customer_state`), not arbitrary CRM/person rows, but
 * it is safe (never resolves to a `firma.id`) and needs no new backend. Flagged for
 * Valerio: a real person-search endpoint (the api's own `keywordPersons`, already
 * used internally by `GET /search`, just isn't exposed as an isolated person-only
 * leg) would remove this narrowing.
 *
 * [Source: apps/web/src/features/entity-detail/components/FirmEditDialog.tsx] (dialog/draft precedent)
 * [Source: apps/web/src/features/administration/components.tsx] (Dialog, Toggle, TextInput, SettingRow)
 * [Source: projects/broker-ag/dms/spec-part2-stefan-delta.md §4.3, §4.6, §4.8]
 */
import { useEffect, useState } from 'react';
import { AlertCircle, Search, X } from 'lucide-react';
import type { CustomerListItemDto, PolicyDto, UpdatePolicyDto } from '@brokly/shared';
import { IGB2B_CODE_LABELS, isValidUid } from '@brokly/shared';
import { Dialog, Button, Toggle, TextInput, SettingRow } from '../../administration/components';
import { ApiError, apiClient } from '../../../lib/api-client';

/** IGB2B Sparte options for the reclassification select (sorted by branchencode). */
const IGB2B_SPARTE_OPTIONS = Object.entries(IGB2B_CODE_LABELS)
  .map(([code, label]) => ({ code, label }))
  .sort((a, b) => a.code.localeCompare(b.code));

/** Debounce window (ms) — mirrors `useDocumentRows`' `DOCUMENTS_SEARCH_DEBOUNCE_MS` precedent. */
const VERMITTLER_SEARCH_DEBOUNCE_MS = 250;

export interface PolicyEditInitial {
  bezeichnung: string | null;
  praemienbefreiung: boolean;
  praemienbefreiungSeit: string | null;
  vermittlerPersonId: string | null;
  bemerkungen: string | null;
  /** P2 Placement Slice 2: the current IGB2B Sparte code (null = unclassified). */
  igb2bCode: string | null;
  /**
   * P5-S7: the insurer UID. A policy without one (the extraction drops an unreadable one) gets the field,
   * set once; a policy with one shows none. Omitted: treated as set (no field).
   */
  carrierUid?: string | null;
}

interface PolicyDraft {
  bezeichnung: string;
  praemienbefreiung: boolean;
  praemienbefreiungSeit: string; // '' = null; native <input type="date"> value shape
  vermittlerPersonId: string | null;
  bemerkungen: string;
  igb2bCode: string; // '' = leave unchanged
  carrierUid: string; // '' = not set
}

function draftFromInitial(d: PolicyEditInitial): PolicyDraft {
  return {
    bezeichnung: d.bezeichnung ?? '',
    praemienbefreiung: d.praemienbefreiung,
    praemienbefreiungSeit: d.praemienbefreiungSeit ?? '',
    vermittlerPersonId: d.vermittlerPersonId,
    bemerkungen: d.bemerkungen ?? '',
    igb2bCode: d.igb2bCode ?? '',
    carrierUid: '',
  };
}

/**
 * P5-S7: the UID typed by the broker in its canonical form `CHE-123.456.789`, when it is one with a valid
 * check digit; null otherwise. Any spelling of the digits is accepted (CHE105941850, che-105.941.850).
 */
export function canonicalCarrierUid(raw: string): string | null {
  const compact = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const m = /^CHE(\d{3})(\d{3})(\d{3})$/.exec(compact);
  if (!m) return null;
  const uid = `CHE-${m[1]}.${m[2]}.${m[3]}`;
  return isValidUid(uid) ? uid : null;
}

/** The api's refusals of the UID, in German. */
function saveErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'carrier_uid_already_set') return 'Die Versicherer-UID ist bereits gesetzt.';
    if (err.code === 'invalid_carrier_uid') return 'Keine gültige UID (CHE-123.456.789, mit gültiger Prüfziffer).';
    if (err.code === 'policy_number_conflict') return 'Beim Versicherer mit dieser UID gibt es bereits eine Police mit dieser Nummer.';
  }
  return err instanceof Error ? err.message : 'Die Police konnte nicht gespeichert werden.';
}

/** The PATCH body: the full editable set (§4.6 — server applies whatever is sent). */
function draftToPatch(draft: PolicyDraft, initial: PolicyEditInitial): UpdatePolicyDto {
  const patch: UpdatePolicyDto = {
    bezeichnung: draft.bezeichnung.trim() === '' ? null : draft.bezeichnung.trim(),
    praemienbefreiung: draft.praemienbefreiung,
    // §4.3 / the api's 400 guard: seit only travels when befreiung is actually true.
    praemienbefreiung_seit:
      draft.praemienbefreiung && draft.praemienbefreiungSeit !== '' ? draft.praemienbefreiungSeit : null,
    vermittler_person_id: draft.vermittlerPersonId,
    bemerkungen: draft.bemerkungen.trim() === '' ? null : draft.bemerkungen.trim(),
  };
  // igb2b_code is reclassification-only (never cleared to null): send it only when it is set AND
  // changed from the stored value.
  if (draft.igb2bCode !== '' && draft.igb2bCode !== (initial.igb2bCode ?? '')) {
    patch.igb2b_code = draft.igb2bCode;
  }
  // P5-S7: the UID only for a policy without one, only when typed (the caller checked it).
  const uid = initial.carrierUid === null ? canonicalCarrierUid(draft.carrierUid) : null;
  if (uid !== null) patch.carrier_uid = uid;
  return patch;
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** Versicherungsvermittler person-picker — search + pick, or clear. */
function VermittlerPicker({
  personId,
  personName,
  onPick,
  onClear,
  disabled,
}: {
  personId: string | null;
  /** Display name for the CURRENTLY set id, when known (the initial-load caller may not have it). */
  personName: string | null;
  onPick: (personId: string, name: string) => void;
  onClear: () => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query.trim(), VERMITTLER_SEARCH_DEBOUNCE_MS);
  const [results, setResults] = useState<CustomerListItemDto[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (debouncedQuery === '') {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    apiClient.customers
      .list({ subject_type: 'person', q: debouncedQuery, limit: 8 })
      .then((res) => {
        if (!cancelled) setResults(res.items);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  if (personId !== null && query === '') {
    return (
      <div className="flex items-center gap-2">
        <span className="flex-1 truncate text-sm">{personName ?? `Person ${personId}`}</span>
        {!disabled && (
          <button
            type="button"
            aria-label="Versicherungsvermittler entfernen"
            onClick={onClear}
            className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <TextInput
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Person suchen …"
          aria-label="Versicherungsvermittler suchen"
          className="pl-8"
        />
      </div>
      {debouncedQuery !== '' && (
        <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-md">
          {searching && (
            <p className="px-3 py-2 text-xs text-muted-foreground">Suche …</p>
          )}
          {!searching && results.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">Keine Treffer.</p>
          )}
          {results.map((r) => (
            <button
              key={r.subject_id}
              type="button"
              onClick={() => {
                onPick(r.subject_id, r.name ?? r.subject_id);
                setQuery('');
              }}
              className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
            >
              {r.name ?? '(ohne Name)'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PolicyEditDialog({
  policyId,
  initial,
  onClose,
  onSaved,
}: {
  policyId: string;
  initial: PolicyEditInitial;
  onClose: () => void;
  /** Called with the refreshed PolicyDto after a successful PATCH. */
  onSaved: (updated: PolicyDto) => void;
}) {
  const [draft, setDraft] = useState<PolicyDraft>(() => draftFromInitial(initial));
  const [vermittlerName, setVermittlerName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uidInvalid = initial.carrierUid === null && draft.carrierUid.trim() !== '' && canonicalCarrierUid(draft.carrierUid) === null;

  async function save() {
    if (uidInvalid) {
      setError('Keine gültige UID (CHE-123.456.789, mit gültiger Prüfziffer).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await apiClient.policies.update(policyId, draftToPatch(draft, initial));
      onSaved(updated);
      onClose();
    } catch (err: unknown) {
      setError(saveErrorText(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      onClose={() => {
        if (!saving) onClose();
      }}
      title="Police bearbeiten"
      description="Manuelle Stammdaten: Bezeichnung, Prämienbefreiung, Versicherungsvermittler, Bemerkungen."
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? 'Wird gespeichert …' : 'Speichern'}
          </Button>
        </>
      }
    >
      {error !== null && (
        <p className="mb-3 flex items-start gap-2 text-sm text-destructive">
          <AlertCircle className="size-4 shrink-0" />
          {error}
        </p>
      )}

      <div className="flex flex-col divide-y divide-border">
        <div className="pb-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Bezeichnung</span>
            <TextInput
              value={draft.bezeichnung}
              disabled={saving}
              onChange={(e) => setDraft((d) => ({ ...d, bezeichnung: e.target.value }))}
              placeholder="z. B. Betriebshaftpflicht · CSS"
              aria-label="Bezeichnung"
            />
          </label>
        </div>

        {initial.carrierUid === null && (
          <div className="py-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Versicherer-UID</span>
              <TextInput
                value={draft.carrierUid}
                disabled={saving}
                onChange={(e) => setDraft((d) => ({ ...d, carrierUid: e.target.value }))}
                placeholder="CHE-123.456.789"
                aria-label="Versicherer-UID"
              />
            </label>
            <p className="mt-1 text-xs text-muted-foreground">
              Nur einmal setzbar. Ist der Kunde aktiv und hat ein unterzeichnetes Brokermandat, wird es dem Versicherer automatisch gemeldet.
              Ohne Versandrecht wartet die Meldung auf die Freigabe durch einen Broker.
            </p>
          </div>
        )}

        <div className="py-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Sparte (IGB2B)</span>
            <select
              value={draft.igb2bCode}
              disabled={saving}
              onChange={(e) => setDraft((d) => ({ ...d, igb2bCode: e.target.value }))}
              aria-label="Sparte (IGB2B)"
              className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Nicht klassifiziert</option>
              {IGB2B_SPARTE_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.code} · {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <SettingRow label="Prämienbefreiung" hint="Die Police ist von der Prämienzahlung befreit.">
          <Toggle
            checked={draft.praemienbefreiung}
            disabled={saving}
            label="Prämienbefreiung"
            onChange={(checked) =>
              setDraft((d) => ({
                ...d,
                praemienbefreiung: checked,
                // Clearing the toggle clears the date too — the api 400s otherwise.
                praemienbefreiungSeit: checked ? d.praemienbefreiungSeit : '',
              }))
            }
          />
        </SettingRow>

        <div className="py-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Prämienbefreiung seit</span>
            <input
              type="date"
              value={draft.praemienbefreiungSeit}
              disabled={saving || !draft.praemienbefreiung}
              onChange={(e) => setDraft((d) => ({ ...d, praemienbefreiungSeit: e.target.value }))}
              aria-label="Prämienbefreiung seit"
              className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-ring"
            />
          </label>
        </div>

        <div className="py-3">
          <span className="mb-1 block text-xs text-muted-foreground">Versicherungsvermittler</span>
          <VermittlerPicker
            personId={draft.vermittlerPersonId}
            personName={vermittlerName}
            disabled={saving}
            onPick={(personId, name) => {
              setDraft((d) => ({ ...d, vermittlerPersonId: personId }));
              setVermittlerName(name);
            }}
            onClear={() => {
              setDraft((d) => ({ ...d, vermittlerPersonId: null }));
              setVermittlerName(null);
            }}
          />
        </div>

        <div className="pt-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Bemerkungen</span>
            <textarea
              value={draft.bemerkungen}
              disabled={saving}
              onChange={(e) => setDraft((d) => ({ ...d, bemerkungen: e.target.value }))}
              rows={3}
              aria-label="Bemerkungen"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </label>
        </div>
      </div>
    </Dialog>
  );
}
